/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/userLists.config.json";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import {
  GET_PRESENTATIONS,
  GET_USER_LIST,
  GET_USER_LIST_ADMIN,
} from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { pretty } from "@vlinder-web/utils-react";
import { StorageModule } from "@vlinder-web/storage-module-react";
import jwt_decode from "jwt-decode";
const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IUserListsMethods extends IPluginMethodMap {
  getUserListsProps(args: any): Promise<any>;
}

export class UserListsService implements IAgentPlugin {
  readonly methods: IUserListsMethods;

  constructor() {
    this.methods = {
      getUserListsProps: this.getUserListsProps.bind(this),
    };
  }

  async getUserListsProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let filter = {};
    console.log("args invitee==>", args);
    const searchType = this.determineSearchType(args?.criteria);
    if (searchType && searchType !== null) {
      if (searchType === "multi") {
        filter = this.getMultiFilter(args?.criteria);
      } else {
        filter = this.convertCriteria(args?.criteria);
      }
    }
    const hasPresentation = filter.hasOwnProperty("presentation");
    const _filter = {
      // status: {
      //   eq: "active",
      // },
      // year: {
      //   eq: "2024",
      // },
    };

    try {
      if (client) {
        const presentation = await client.query({
          query: GET_PRESENTATIONS,
          variables: {
            filter: _filter,
            options: {
              limit: 0,
              skip: 0,
              sort: {
                field: "status",
                order: "asc",
              },
            },
          },
          fetchPolicy: "network-only",
        });

        let defaultPresentation =
          presentation?.data?.getPresentations?.content[0]?.id;
        if (!hasPresentation || Object.keys(args).length === 0) {
          filter = {
            ...filter,
            presentation: {
              eq: defaultPresentation,
            },
          };
        }
        console.log("invitee filter=>", pretty(filter));
        const storageSrv = StorageModule.getInstance();
        let _sessionToken = storageSrv.get(
          "userProfile",
          "session"
        )?.accessToken;
        let _localToken = storageSrv.get("userProfile", "local")?.accessToken;
        let _token = _localToken ?? _sessionToken;
        const tokenRole = jwt_decode(_token);
        const res = await client.query({
          query:
            tokenRole?.currentRole?.name === "production"
              ? GET_USER_LIST
              : GET_USER_LIST_ADMIN,
          variables: {
            filters: filter,
            options: {
              limit: args?.size ?? 10,
              skip: args?.offset ?? 0,
              sort: {
                field: "updatedAt",
                order: "desc",
              },
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data) {
          let _totalPages = res?.data?.getReferals?.count
            ? parseInt(res?.data?.getReferals?.count) / 10
            : 1;

          let _currentPage =
            res?.data?.getReferals?.skip / res?.data?.getReferals?.limit + 1;
          let _rowData = this.formatUserTable(res?.data?.getReferals?.content);
          return {
            ...config,
            searchPresentationFilter:
              presentation?.data?.getPresentations?.content?.map(
                (_item: any) => {
                  return { value: _item?.id, label: _item?.name };
                }
              ),
            presentationFilter:
              presentation?.data?.getPresentations?.content?.map(
                (_item: any) => {
                  return { id: _item?.id, label: _item?.name };
                }
              ),
            ultUsers: _rowData,
            isEmpty: res?.data?.getReferals?.content?.length <= 0,
            activePage: _currentPage,
            currentPage: _currentPage,
            pageSize: _rowData.length,
            totalCount: res?.data?.getReferals?.count,
            totalPages: _totalPages,
          };
        }
      }
    } catch (err: any) {
      console.log(`error in table data`, err);
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private determineSearchType(criteria: any) {
    if (Object.keys(criteria).length === 0) {
      return null;
    } else if (Object.keys(criteria)[0] === "0") {
      return "multi";
    } else {
      return "normal";
    }
  }

  private getMultiFilter(criteria: any) {
    const transformedCriteria: any = {};
    for (const key in criteria) {
      if (criteria.hasOwnProperty(key)) {
        const { key: filterKey, filters } = criteria[key];
        const { inputData } = filters;
        const filterType = Object.keys(filters).find(
          (key) => key !== "inputData"
        );

        const filterValue = filters[filterType];

        // if (
        //   inputData.id === "Box___boxStatus"
        //   // ||
        //   // inputData.id === "Box___paymentStats"
        // ) {
        //   // If the id is "Boxes___boxStatus", assign the value directly
        //   transformedCriteria[inputData.id] = filterValue;
        // } else {
        // Convert "true" and "false" strings to actual boolean values
        if (inputData.id === "InviteBy___fullName") {
          transformedCriteria[inputData.id] = {
            [filterType.toLowerCase().replace("equal", "contains")]:
              filterValue,
          };
        } else if (
          typeof filterValue === "string" &&
          filterValue.toLowerCase() === "true"
        ) {
          transformedCriteria[inputData.id] = {
            [filterType.toLowerCase().replace("equal", "eq")]: true,
          };
        } else if (
          typeof filterValue === "string" &&
          filterValue.toLowerCase() === "false"
        ) {
          transformedCriteria[inputData.id] = {
            [filterType.toLowerCase().replace("equal", "eq")]: false,
          };
        } else {
          transformedCriteria[inputData.id] = {
            [filterType.toLowerCase().replace("equal", "eq")]: filterValue,
          };
        }
        // }
      }
    }
    return transformedCriteria;
  }

  private convertCriteria(criteria: any) {
    const result: any = {};
    for (const key in criteria) {
      console.log("result==>", key);
      if (
        key !== "Box___boxStatus" &&
        key !== "presentation" &&
        key !== "User___inviteStatus" &&
        key !== "Box___paymentStats"
      ) {
        result[key] = { contains: criteria[key] };
      } else {
        result[key] = { eq: criteria[key] };
      }
    }
    return result;
  }

  private formatUserTable(data: any) {
    console.log(`databbbbb`, data);
    let _rowData: any = [];
    data &&
      data.forEach((item?: any) => {
        _rowData.push({
          id: item?.user?.id,
          rid: item?.id,
          vuid: item?.user?.vuid,
          masqId: item?.user?.vuid,
          boxStatus: item?.box?.boxStatus ?? "-",
          section: item?.sections?.name ?? "-",
          sectionOptType: item?.sectionOptType ?? "-",
          partition: item?.partition ?? "-",
          inviteStatus: item?.inviteStatus ?? "-",
          inviteType: item?.inviteType ?? "-",
          amountPaid: item?.box?.amountPaid
            ? item?.box?.amountPaid?.toFixed(2)
            : 0,
          note: item?.box?.note ?? "-",
          totalAmount: item?.box?.totalAmount
            ? item?.box?.totalAmount.toFixed(2)
            : 0,
          paymentStatus: item?.box?.paymentStats ?? "-",
          cosId: item?.box?.costumeID ?? "-",
          userStatus: this.getFormattedStatus(
            item?.user?.accountStatus,
            item?.user?.suspended
          ),
          sectionId: item?.sections?.id ?? "-",
          presentationName: item?.presentation?.name ?? "",
          presentationId: item?.presentation?.id ?? "",
          name: `${
            item?.user?.firstName && item?.user?.firstName !== ""
              ? item?.user?.firstName
              : "-"
          } ${
            item?.user?.lastName && item?.user?.lastName !== ""
              ? item?.user?.lastName
              : " "
          }`,
          email: item?.user?.email ?? "-",
          gender: item?.user?.gender ?? "-",
          verified: item?.user?.emailVerified,
          invitedBy:
            item?.invitedBy &&
            item?.invitedBy.length > 0 &&
            item?.invitedBy[0]?.fullName &&
            item?.invitedBy[0]?.fullName !== ""
              ? item?.invitedBy[0]?.fullName
              : "-",
          createdAt: this.formatCreatedDate(item?.createdAt) ?? "-",
          inviteNote: item?.inviteNote ?? "-",
          expiresOn: item?.expiresOn,
          discountCode: item?.discountCode?.discountCode ?? "-",
          discountStatus: item?.discountCode?.status ?? "Not Applicable",
        });
      });
    console.log(`databbbbb1`, _rowData);
    return _rowData;
  }

  private formatCreatedDate(date: Date) {
    if (date) {
      return moment(date).format("LL");
    }
  }
  private getFormattedStatus(status: string, suspended: boolean) {
    if (suspended) {
      return "suspended";
    } else {
      switch (status) {
        case "USER_ADDED":
          return "created";
        case "USER_INVITED":
          return "invited";
        case "USER_VERIFIED":
          return "verified";
        case "USER_ACC_CREATED":
          return "active";
        case "SUSPENDED":
          return "suspended";
        default:
          return "-";
      }
    }
  }
}
